/**
 * useSourcing — the shared data layer for the sourcing feature, so the desktop
 * route and the mobile screen drive the SAME logic (no parity drift). Loads the
 * open-orders book, places limit orders, and runs the confirm handshake.
 *
 * The confirm reprice handshake lives in the ConfirmFill component (it owns the
 * "the price moved — re-confirm the new total" state); this hook exposes the raw
 * `confirm` call plus a book refresher.
 */
import { useCallback, useEffect, useState } from 'react'
import { sourcingApi } from '../api/endpoints'
import { ApiError } from '../api/types'
import type { SearchFilters } from '../api/types'
import { SOURCING_PLAN, type SourcingOrderView, type SourcingLimitBasis } from './sourcing'

export interface UseSourcing {
  live: boolean
  loading: boolean
  error: string | null
  orders: SourcingOrderView[]
  member: boolean
  refresh: () => Promise<void>
  place: (args: {
    spec: SearchFilters
    limitBasis: SourcingLimitBasis
    limitValue: number
    certifiedOnly?: boolean
  }) => Promise<{ ok: boolean; error?: string }>
  cancel: (id: string) => Promise<void>
}

export function useSourcing(): UseSourcing {
  const [live, setLive] = useState<boolean>(SOURCING_PLAN.live)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [orders, setOrders] = useState<SourcingOrderView[]>([])
  const [member, setMember] = useState(false)

  const refresh = useCallback(async () => {
    setError(null)
    try {
      const res = await sourcingApi.listOrders()
      setLive(res.configured)
      setOrders(res.configured && res.orders ? res.orders : [])
    } catch (err) {
      // A 503 while an index builds, or a transient failure — show a soft message,
      // never a hard crash (the feature is additive).
      if (err instanceof ApiError && err.status === 503) {
        setError('Your sourcing orders are being prepared — try again in a moment.')
      } else {
        setError('Could not load your sourcing orders.')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  // Keep `live` in step if the boot /health hydration lands after first paint.
  useEffect(() => {
    const onChange = () => setLive(SOURCING_PLAN.live)
    window.addEventListener('aska:sourcing-changed', onChange)
    return () => window.removeEventListener('aska:sourcing-changed', onChange)
  }, [])

  const place = useCallback<UseSourcing['place']>(async (args) => {
    try {
      const res = await sourcingApi.place({
        spec: args.spec,
        limitBasis: args.limitBasis,
        limitValue: args.limitValue,
        certifiedOnly: args.certifiedOnly ?? true,
        origin: 'ticket',
      })
      if (!res.configured) return { ok: false, error: 'Sourcing isn’t switched on yet.' }
      if (typeof res.member === 'boolean') setMember(res.member)
      await refresh()
      return { ok: true }
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { code?: string; error?: string } | undefined
        if (body?.code === 'kyc_required') return { ok: false, error: 'Your account must be verified before you can source a diamond.' }
        if (body?.code === 'sourcing_limit_reached') return { ok: false, error: body.error ?? 'You’ve reached your open-order limit.' }
        return { ok: false, error: body?.error ?? 'Could not place your order.' }
      }
      return { ok: false, error: 'Could not place your order.' }
    }
  }, [refresh])

  const cancel = useCallback(async (id: string) => {
    try { await sourcingApi.cancel(id) } finally { await refresh() }
  }, [refresh])

  return { live, loading, error, orders, member, refresh, place, cancel }
}
